import { TestimonialForm } from '@/components/testimonials/TestimonialForm'
import { TestimonialList } from '@/components/testimonials/TestimonialList'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import { Testimonial } from '@/types/api'
import { X } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'

export default function TestimonialsPage() {
  const { toast } = useToast()
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [showForm, setShowForm] = useState(false)
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | undefined>(undefined)

  const fetchTestimonials = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiClient.getTestimonials({
        page,
        per_page: 10,
        search,
      })
      setTestimonials(response.data || [])
      if (response.meta) {
        setTotalPages(response.meta.last_page)
        setTotalItems(response.meta.total)
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to fetch testimonials',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [page, search, toast])

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTestimonials()
    }, 300)
    return () => clearTimeout(timer)
  }, [fetchTestimonials])

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return

    try {
      await apiClient.deleteTestimonial(id)
      toast({
        title: 'Success',
        description: 'Testimonial deleted successfully',
      })
      fetchTestimonials()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete testimonial',
        variant: 'destructive',
      })
    }
  }

  const handleToggleStatus = async (id: string) => {
    try {
      await apiClient.toggleTestimonialStatus(id)
      toast({
        title: 'Success',
        description: 'Testimonial status updated',
      })
      fetchTestimonials()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update status',
        variant: 'destructive',
      })
    }
  }

  const handleEdit = (testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial)
    setShowForm(true)
  }

  const handleCreate = () => {
    setSelectedTestimonial(undefined)
    setShowForm(true)
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    fetchTestimonials()
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    setPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Manual Testimonials</h1>
          <p className="text-muted-foreground">Manage homepage testimonials</p>
        </div>
      </div>

      <TestimonialList
        testimonials={testimonials}
        isLoading={loading}
        searchTerm={search}
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        onSearchChange={handleSearchChange}
        onPageChange={handlePageChange}
        onAddTestimonial={handleCreate}
        onEditTestimonial={handleEdit}
        onDeleteTestimonial={handleDelete}
        onToggleStatus={handleToggleStatus}
      />

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedTestimonial ? 'Edit Testimonial' : 'Create Manual Testimonial'}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <TestimonialForm
                testimonial={selectedTestimonial}
                onSuccess={handleFormSuccess}
                onCancel={() => setShowForm(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
